// src/components/CookieConsentBanner.js
import React, { useState, useEffect } from 'react';
import {
    Box, Button, Checkbox, Slide, Stack, Text, useDisclosure, VStack, Heading
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { loadContentSquare } from "../utils/loadContentSquare";
import { loadMetaPixel } from "../utils/loadMetaPixel";
import { loadCrisp, nascondiCrisp, mostraCrisp } from "../utils/loadCrisp";
import {
    CATEGORIE, COOKIE_CONSENT_KEY, COOKIE_CONSENT_EVENT, OPEN_PREFERENCES_EVENT,
    categorieInUso, leggiConsenso, strumentoConsentito,
} from "../utils/cookieConsent";

// Come si attiva ogni strumento: la categoria di appartenenza sta in
// utils/cookieConsent.js (STRUMENTI), non qui.
const LOADERS = {
    crisp: loadCrisp,
    contentsquare: loadContentSquare,
    metaPixel: loadMetaPixel,
};

const NESSUN_CONSENSO = { required: true, preferences: false, analytics: false, social: false, ads: false };

const aggiornaConsentMode = (prefs) => {
    if (!window.gtag) return;
    window.gtag('consent', 'update', {
        analytics_storage: prefs.analytics ? 'granted' : 'denied',
        ad_storage: prefs.ads ? 'granted' : 'denied',
        ad_user_data: prefs.ads ? 'granted' : 'denied',
        ad_personalization: prefs.ads ? 'granted' : 'denied',
    });
};

// Attiva tutti gli strumenti consentiti dalle preferenze date
const applicaConsenso = (prefs) => {
    Object.entries(LOADERS).forEach(([strumento, load]) => {
        if (strumentoConsentito(strumento, prefs)) load();
    });
    aggiornaConsentMode(prefs);
    if (strumentoConsentito('crisp', prefs)) mostraCrisp();
};

export default function CookieConsentBanner() {
    const [consentGiven, setConsentGiven] = useState(false);
    const [showPreferences, setShowPreferences] = useState(false);
    const { onOpen } = useDisclosure();

    const [preferences, setPreferences] = useState(NESSUN_CONSENSO);

    useEffect(() => {
        const stored = leggiConsenso();

        if (stored) {
            setPreferences({ ...NESSUN_CONSENSO, ...stored });
            setConsentGiven(true);
            applicaConsenso(stored);
        } else {
            if (localStorage.getItem(COOKIE_CONSENT_KEY)) {
                console.error('Cookie consent data corrupted, resetting');
                localStorage.removeItem(COOKIE_CONSENT_KEY);
            }
            // Gli strumenti tecnici partono comunque (la chat resta disponibile),
            // ma la bolla Crisp resta nascosta finche' il banner e' aperto:
            // su mobile copriva il pulsante "Gestisci preferenze".
            applicaConsenso(NESSUN_CONSENSO);
            nascondiCrisp();
            onOpen();
        }
    }, [onOpen]);

    // Riapertura dal link "Gestisci i cookie" (es. pagina Cookie Policy)
    useEffect(() => {
        const riapri = () => {
            const stored = leggiConsenso();
            setPreferences({ ...NESSUN_CONSENSO, ...(stored || {}) });
            setShowPreferences(true);
            setConsentGiven(false);
            nascondiCrisp();
        };
        window.addEventListener(OPEN_PREFERENCES_EVENT, riapri);
        return () => window.removeEventListener(OPEN_PREFERENCES_EVENT, riapri);
    }, []);


    const savePreferences = (prefs) => {
        const precedenti = leggiConsenso();
        localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(prefs));

        // Avvisa FloatingCTA (e chiunque altro ascolti) che il banner e' stato
        // risolto, cosi' puo' smettere di restare nascosto in attesa di una
        // risposta dell'utente. localStorage da solo non basta: l'evento
        // 'storage' nativo non scatta nello stesso tab che ha scritto il valore.
        window.dispatchEvent(new Event(COOKIE_CONSENT_EVENT));

        setConsentGiven(true);
        setShowPreferences(false);

        // Revoca: uno script gia' caricato non si puo' "scaricare". Se l'utente
        // toglie un consenso dato in precedenza, ricarichiamo la pagina cosi'
        // lo strumento non viene piu' avviato.
        const revocato = precedenti && Object.keys(CATEGORIE).some((c) => precedenti[c] && !prefs[c]);
        if (revocato) {
            window.location.reload();
            return;
        }

        applicaConsenso(prefs);
    };


    const handleAcceptAll = () => {
        const allPrefs = { required: true, preferences: true, analytics: true, social: true, ads: true };
        setPreferences(allPrefs);
        savePreferences(allPrefs);
    };


    const handleRejectAll = () => {
        setPreferences(NESSUN_CONSENSO);
        savePreferences(NESSUN_CONSENSO);
    };

    if (consentGiven) return null;

    return (
        <Slide direction="bottom" in={!consentGiven} style={{ zIndex: 999 }}>
            <Box
                bg="gray.400"
                color="white"
                p={4}
                textAlign="center"
                position="fixed"
                bottom={0}
                width="100%"
                boxShadow="0 -4px 10px rgba(0,0,0,0.3)"
                maxH="90vh"
                overflowY="auto"
            >
                {!showPreferences ? (
                    <>
                        <Text mb={2} fontSize={["sm", "md"]}>
                            Utilizziamo cookie tecnici e, solo con il tuo consenso, cookie analitici e di marketing. {' '}
                            <RouterLink to="/cookiepolicy" style={{ textDecoration: 'underline', color: '#FFA200' }}>
                                Cookie Policy
                            </RouterLink>.
                        </Text>
                        <Stack direction={["column", "row"]} justify="center" spacing={3} mt={3}>
                            <Button colorScheme="green" onClick={handleAcceptAll} size={["sm", "md"]}>Accetta tutto</Button>
                            <Button colorScheme="blackAlpha" onClick={handleRejectAll} size={["sm", "md"]}>Rifiuta tutto</Button>
                            <Button variant="outline" colorScheme="white" onClick={() => setShowPreferences(true)} size={["sm", "md"]}>Gestisci preferenze</Button>
                        </Stack>
                    </>
                ) : (
                    <VStack spacing={3} align="stretch">
                        <Heading size="sm">Preferenze cookie</Heading>
                        <Checkbox isChecked isDisabled>{CATEGORIE.required}</Checkbox>
                        {categorieInUso().map((cat) => (
                            <Checkbox
                                key={cat}
                                isChecked={!!preferences[cat]}
                                onChange={(e) => setPreferences(p => ({ ...p, [cat]: e.target.checked }))}
                            >
                                {CATEGORIE[cat]}
                            </Checkbox>
                        ))}

                        <Stack direction={["column", "row"]} justify="center" spacing={3} pt={2}>
                            <Button colorScheme="orange" onClick={handleAcceptAll} size="sm">Accetta tutto</Button>
                            <Button colorScheme="white" onClick={handleRejectAll} size="sm">Rifiuta tutto</Button>
                            <Button colorScheme="blue" onClick={() => savePreferences(preferences)} size="sm">Salva impostazioni</Button>
                            <Button variant="outline" onClick={() => setShowPreferences(false)} size="sm">Indietro</Button>
                        </Stack>
                    </VStack>
                )}
            </Box>
        </Slide>
    );
}